import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { faCircleInfo, faRadar } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { useGetMonthlySignalsQuery } from "../../../redux/analytics/analytics.api";

import { AnalyticsFilterBar } from "../components/AnalyticsFilterBar";
import { AnalyticsEmptyState } from "../components/AnalyticsEmptyState";
import { AnalyticsSkeleton } from "../components/AnalyticsSkeleton";
import { AnalyticsSelection } from "../hooks/useAnalyticsSelection";
import { SignalMonthRow, SignalScope } from "../schema/signals";
import { serializeDateRange } from "../utils/analyticsUrlState";
import { formatNumber } from "../utils/percentage";

export interface SignalsTabProps {
	selection: AnalyticsSelection;
}

const formatRate = (value: number | null): string =>
	value === null ? "—" : `${value.toFixed(1)}%`;

const CELL = "px-4 py-2.5 text-right tabular-nums whitespace-nowrap";
const HEAD =
	"px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-meta whitespace-nowrap";

/** "2026-08" → "Aug 2026", without constructing a date that could shift by timezone. */
const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

export const formatMonthKey = (monthKey: string): string => {
	const [year, month] = monthKey.split("-");
	const label = MONTH_LABELS[Number(month) - 1];

	return label ? `${label} ${year}` : monthKey;
};

/** A cell showing a headline count with the unique-company count beneath it. */
const CountWithCompanies: FunctionComponent<{
	value: number;
	companies: number;
	label: string;
}> = ({ value, companies, label }) => (
	<span className="flex flex-col items-end leading-tight">
		<span className="font-semibold text-text">{formatNumber(value)}</span>
		<span className="text-[10px] text-text-meta">
			{formatNumber(companies)} {label}
		</span>
	</span>
);

/**
 * Explains an all-zero report caused by the *selection* rather than by a lack of signals.
 *
 * Signals only exist for tracked lists, so scoping this report to a list with no profile
 * tracker yields twelve rows of zeros — which reads as "you have no signals" when the
 * truth is "this list is not tracked". Saying which it is, is the whole point.
 */
const ScopeNotice: FunctionComponent<{ scope: SignalScope }> = ({ scope }) => {
	const { t } = useTranslation();

	if (!scope.companyHasTrackers) {
		return <Notice text={t("analytics.signals.noTrackersAtAll")} />;
	}

	const selected = scope.requestedListIds.length;

	// No list selected means "every tracker of the company" — nothing to explain.
	if (selected === 0) {
		return null;
	}

	if (scope.trackedListIds.length === 0) {
		return <Notice text={t("analytics.signals.untrackedSelection", { count: selected })} />;
	}

	if (scope.trackedListIds.length < selected) {
		return (
			<Notice
				text={t("analytics.signals.partiallyTracked", {
					tracked: scope.trackedListIds.length,
					selected,
				})}
			/>
		);
	}

	return null;
};

const Notice: FunctionComponent<{ text: string }> = ({ text }) => (
	<div
		className="flex flex-row items-start gap-2 w-full rounded-xl border border-status-warning-light bg-status-warning-light/30 px-4 py-3 mb-4"
		data-testid="signals-scope-notice"
	>
		<Icon className="w-3.5 h-3.5 mt-0.5 text-status-warning shrink-0" icon={faCircleInfo} />
		<span className="text-xs leading-relaxed text-text-meta">{text}</span>
	</div>
);

/**
 * Profile Tracker Signals, by month: how much is being tracked, what it produced, and
 * whether the outreach from those signals landed.
 */
export const SignalsTab: FunctionComponent<SignalsTabProps> = ({ selection }) => {
	const { t } = useTranslation();

	// The tracker report follows the page's selection. The period is not a row filter here
	// — the months *are* the time axis, so it moves and resizes that axis instead.
	const listIds = useMemo(
		() =>
			selection.selectedLists
				.map((list) => list.id)
				.filter((id): id is number => typeof id === "number"),
		[selection.selectedLists]
	);

	const period = useMemo(
		() => serializeDateRange(selection.dateFromToValue),
		[selection.dateFromToValue]
	);

	const { data, isLoading } = useGetMonthlySignalsQuery({
		listIds,
		from: period.from ?? undefined,
		to: period.to ?? undefined,
		senderUserIds: selection.senderUserIds.length > 0 ? selection.senderUserIds : undefined,
	});

	if (isLoading) {
		return <AnalyticsSkeleton />;
	}

	if (!data) {
		return <AnalyticsEmptyState />;
	}

	const currentMonthKey = data.months[data.months.length - 1]?.monthKey;

	return (
		<>
			<AnalyticsFilterBar
				campaigns={selection.campaigns}
				dateFromToValue={selection.dateFromToValue}
				listOptions={selection.listOptions}
				selectedLists={selection.selectedLists}
				senderUserIds={selection.senderUserIds}
				onCampaignToggle={selection.onCampaignToggle}
				onDateChange={selection.onDateChange}
				onListContentFilterToggle={selection.onListContentFilterToggle}
				onListSelect={selection.onListSelect}
				onSenderChange={selection.onSenderChange}
				// A campaign filter has nothing to narrow here: the report is scoped by tracker,
				// and a tracker belongs to a list rather than to any one campaign.
				hide={{ campaign: true }}
			/>

			<div className="flex flex-col w-full mt-4">
				<ScopeNotice scope={data.scope} />
				{selection.senderUserIds.length > 0 ? (
					<Notice text={t("analytics.signals.senderScopeNote")} />
				) : null}
				<div className="rounded-xl border border-border-light bg-brand-white overflow-hidden">
					<div className="flex flex-row items-center gap-3 px-4 py-3 border-b border-border-light">
						<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-action-200 text-brand-light">
							<Icon className="w-4 h-4" icon={faRadar} />
						</div>
						<div className="text-sm font-semibold text-text">{t("analytics.signals.title")}</div>
						<div className="text-xs text-text-meta">· {t("analytics.signals.byMonth")}</div>
					</div>

					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="bg-background-light">
									<th
										className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-text-meta"
										rowSpan={2}
									>
										{t("analytics.signals.columns.month")}
									</th>
									<th
										className={classNames(HEAD, "text-center border-l border-border-light")}
										colSpan={2}
									>
										{t("analytics.signals.groups.tracking")}
									</th>
									<th
										className={classNames(HEAD, "text-center border-l border-border-light")}
										colSpan={5}
									>
										{t("analytics.signals.groups.outreach")}
									</th>
								</tr>
								<tr className="bg-background-light">
									<th className={classNames(HEAD, "border-l border-border-light")}>
										{t("analytics.signals.columns.trackedProfiles")}
									</th>
									<th className={HEAD}>{t("analytics.signals.columns.newSignals")}</th>
									<th className={classNames(HEAD, "border-l border-border-light")}>
										{t("analytics.signals.columns.leadsContacted")}
									</th>
									<th className={HEAD}>{t("analytics.signals.columns.leadsEnded")}</th>
									<th className={HEAD}>{t("analytics.signals.columns.replyRate")}</th>
									<th className={HEAD}>{t("analytics.signals.columns.positiveReplies")}</th>
									<th className={HEAD}>{t("analytics.signals.columns.sentiment")}</th>
								</tr>
							</thead>
							<tbody>
								{data.months.map((month: SignalMonthRow) => (
									<tr
										key={month.monthKey}
										className="border-b border-border-light last:border-b-0 hover:bg-background-light"
									>
										<td
											className={classNames(
												"px-4 py-2.5 text-left font-semibold whitespace-nowrap",
												month.monthKey === currentMonthKey ? "text-brand-light" : "text-text"
											)}
										>
											{formatMonthKey(month.monthKey)}
										</td>
										<td className={classNames(CELL, "border-l border-border-light")}>
											<CountWithCompanies
												companies={month.trackedCompanies}
												label={t("analytics.signals.uniqueCompanies")}
												value={month.trackedProfiles}
											/>
										</td>
										<td className={CELL}>
											<CountWithCompanies
												companies={month.signalCompanies}
												label={t("analytics.signals.uniqueCompanies")}
												value={month.newSignals}
											/>
										</td>
										<td className={classNames(CELL, "border-l border-border-light")}>
											{formatNumber(month.leadsContacted)}
										</td>
										<td className={CELL}>{formatNumber(month.leadsEnded)}</td>
										<td className={CELL}>{formatRate(month.replyRate)}</td>
										<td className={classNames(CELL, "text-status-success")}>
											{formatNumber(month.positiveReplies)}
										</td>
										<td className={CELL}>{formatRate(month.sentimentRatio)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>

				<p className="text-xs text-text-meta mt-3">{t("analytics.signals.footnote")}</p>
			</div>
		</>
	);
};
